// src/components/SearchModal/Filters.jsx
import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ItemCard from "./ItemCard";
import LabelCheckbox from "./LabelCheckbox";
import Loading from "./../../components/Loading";
import { fetchItems } from "../../utils/fetchItems"; // 共通のfetchItems関数をインポート
import useSortedItems from "../../hooks/useSortedItems"; // 商品のソートロジック

// デバウンス関数
const debounce = (func, wait) => {
 let timeout;
 return (...args) => {
  clearTimeout(timeout);
  timeout = setTimeout(() => func(...args), wait);
 };
};

// クラス操作の抽象化関数
const updateClassOnScroll = (element, className) => {
 if (element.scrollTop === 0) {
  element.classList.remove(className);
 } else {
  element.classList.add(className);
 }
};

function Filters() {
 const [checkboxes, setCheckboxes] = useState([]);
 const [loading, setLoading] = useState(true);
 const [filteredItems, setFilteredItems] = useState([]);
 const [isSfOpen, setIsSfOpen] = useState(false);
 const [ItemsData, setItemsData] = useState([]);
 const location = useLocation();
 const navigate = useNavigate();

 // スクロール時にクラスを追加する関数
 const handleScroll = (event) => {
  updateClassOnScroll(event.target, "is-scrolled");
 };
 // デバウンスされたスクロール関数
 const debouncedHandleScroll = debounce(handleScroll, 10);

 // jsonデータのキャッシュ未対応
 useEffect(() => {
  setLoading(true);
  fetchItems() // 共通のfetchItems関数を使用
   .then((data) => {
    setItemsData(data);
    setLoading(false);
    initializeCheckboxes(data);
   })
   .catch((error) => {
    console.error("Error:", error);
    setLoading(false);
   });
 }, []);

 // URLパラメータの初期化
 const initializeCheckboxes = (data) => {
  const params = new URLSearchParams(location.search);
  const initialCheckboxes = [
   ...extractFilterOptions(data, "item_heel_range"),
   ...extractFilterOptions(data, "item_heel_cm"),
   ...extractFilterOptions(data, "item_width_range"),
   ...extractFilterOptions(data, "item_width_e"),
   ...extractFilterOptions(data, "item_toe"),
   ...extractFilterOptions(data, "item_material"),
   ...extractFilterOptions(data, "item_insole"),
   ...extractFilterOptions(data, "item_color_attribute_1"),
   ...extractFilterOptions(data, "item_color_attribute_2"),
   ...extractNestedFilterOptions(data, "shape", ["shape_1", "shape_2"]),
   ...extractNestedFilterOptions(data, "design_1", ["design_1_1", "design_1_2", "design_1_3", "design_1_4"]),
   ...extractNestedFilterOptions(data, "design_2", ["design_2_1", "design_2_2", "design_2_3", "design_2_4", "design_2_5"]),
   ...extractNestedFilterOptions(data, "features", ["features_1", "features_2", "features_3", "features_4", "features_5", "features_6", "features_7", "features_8", "features_9"]),
   ...extractNestedFilterOptions(data, "freeword", ["freeword_1", "freeword_2", "freeword_3", "freeword_4", "freeword_5", "freeword_6", "freeword_7", "freeword_8"]),
  ];

  const updatedCheckboxes = initialCheckboxes.map((checkbox) => {
   const values = params.getAll(checkbox.group);
   checkbox.checked = values.includes(checkbox.checkbox_name);
   return checkbox;
  });

  setCheckboxes(updatedCheckboxes);
 };

 // データ属性を持つボタン要素を取得し、クリック時の処理
 useEffect(() => {
  const handleButtonClick = (event) => {
   const paramsString = event.currentTarget.dataset.params;
   if (paramsString) {
    const params = JSON.parse(paramsString);
    const searchParams = new URLSearchParams(); // 既存のパラメータを削除するために新規作成
    // const searchParams = new URLSearchParams(location.search);
    Object.keys(params).forEach((key) => {
     params[key].forEach((value) => {
      searchParams.append(key, value);
     });
    });
    setIsSfOpen(false);
    const modalSfContainer = document.querySelector("#modal_search_filters_container");
    modalSfContainer.classList.add("is-open");
    navigate({ search: searchParams.toString() }, { replace: true });
   }
  };

  const searchParamsButtons = document.querySelectorAll(".sf-search-params-button");
  searchParamsButtons.forEach((button) => {
   button.addEventListener("click", handleButtonClick);
  });

  return () => {
   searchParamsButtons.forEach((button) => {
    button.removeEventListener("click", handleButtonClick);
   });
  };
 }, [navigate]);

 // チェックボックスの初期化とURLパラメータの更新と同時にチェックボックスの状態を更新
 useEffect(() => {
  if (ItemsData.length > 0) {
   initializeCheckboxes(ItemsData);
  }
 }, [location.search, ItemsData]);

 const orders = {
  heelRange: ["ヒール低め（～4.5cm）", "ヒール高め（5.0cm～）"],
  heelCm: ["1.0cm", "2.0cm", "2.3cm", "2.5cm", "3.0cm", "3.5cm", "4.0cm", "4.5cm", "5.0cm", "6.0cm", "6.5cm", "7.0cm"],
  widthRange: ["標準&ややゆったり（2E･3E）", "ゆったり（4E･5E）"],
  widthE: ["2E", "3E", "4E", "5E", "その他"],
  colorAttribute1: ["黒", "黒以外"],
  colorAttribute2: ["ホワイト系", "ベージュ系", "グレー系", "ダークカラー", "メタリック", "ブルー系", "グリーン系", "ピンク系"],
  toe: ["スクエアトゥ", "ラウンドトゥ", "アーモンドトゥ", "ポインテッドトゥ"],
  material: ["合成皮革", "本革", "ニット", "エナメル"],
  shape: ["ノーマルヒール&フラット", "快適ソール（ウェッジ・スニーカー・ドライビング）"],
  design_1: ["黒パンプス", "プレーン", "ブーツ", "スニーカー"],
  design_2: ["飾り付", "ストラップ・ベルト付", "ローファータイプ", "シューレースタイプ", "モカシンタイプ"],
  features: ["抗菌防臭", "防水", "撥水", "防滑", "高屈曲", "軽量", "インヒール", "かかとクッション&滑り止め", "洗える"],
  freeword: ["OFFも使える", "スタイルアップ", "きれい&かわいい", "エレガント", "就活におすすめ", "スポーティ", "雨の日にぴったり", "スタンダードデザイン"],
 };

 // 表示順序を定義
 const resultOrder = [
  "shape",
  "item_heel_range",
  "item_heel_cm",
  "item_width_range",
  "item_width_e",
  "item_color_attribute_1",
  "item_color_attribute_2",
  "item_toe",
  "item_material",
  "item_insole",
  "design_1",
  "design_2",
  "features",
  "freeword",
 ];

 const extractFilterOptions = useCallback(
  (data, key) => {
   let options = data.map((item) => item[key] || "").filter((value, index, self) => self.indexOf(value) === index);

   if (key === "item_heel_range") {
    options.sort((a, b) => orders.heelRange.indexOf(a) - orders.heelRange.indexOf(b));
   } else if (key === "item_heel_cm") {
    options.sort((a, b) => orders.heelCm.indexOf(a) - orders.heelCm.indexOf(b));
   } else if (key === "item_width_range") {
    options.sort((a, b) => orders.widthRange.indexOf(a) - orders.widthRange.indexOf(b));
   } else if (key === "item_width_e") {
    options.sort((a, b) => orders.widthE.indexOf(a) - orders.widthE.indexOf(b));
   } else if (key === "item_color_attribute_1") {
    options.sort((a, b) => orders.colorAttribute1.indexOf(a) - orders.colorAttribute1.indexOf(b));
   } else if (key === "item_color_attribute_2") {
    options.sort((a, b) => orders.colorAttribute2.indexOf(a) - orders.colorAttribute2.indexOf(b));
   } else if (key === "item_toe") {
    options.sort((a, b) => orders.toe.indexOf(a) - orders.toe.indexOf(b));
   } else if (key === "item_material") {
    options.sort((a, b) => orders.material.indexOf(a) - orders.material.indexOf(b));
   } else if (key in orders) {
    options.sort((a, b) => orders[key].indexOf(a) - orders[key].indexOf(b));
   }

   options = options.filter((option) => option);

   return options.map((option, index) => {
    const allCount = data.filter((item) => item[key] === option).length;
    return {
     checkbox_name: option,
     id: `${key}-${index}`,
     checked: false,
     count: 0,
     allCount: allCount,
     group: key,
    };
   });
  },
  [orders]
 );

 const extractNestedFilterOptions = useCallback((data, key, subKeys) => {
  let options = [];

  subKeys.forEach((subKey) => {
   const subOptions = data
    .map((item) => item[key]?.[subKey])
    .filter((value) => value)
    .filter((value, index, self) => self.indexOf(value) === index);

   options = [
    ...options,
    ...subOptions.map((option, index) => {
     const allCount = data.filter((item) => item[key]?.[subKey] === option).length;
     return {
      checkbox_name: option,
      id: `${key}-${subKey}-${index}`,
      checked: false,
      count: 0,
      allCount: allCount,
      group: `${key}-${subKey}`,
     };
    }),
   ];
  });

  return options;
 }, []);

 const filterGroups = [
  {
   name: "形状",
   checkboxes: checkboxes
    .filter((checkbox) => checkbox.group?.startsWith("shape"))
    .map((checkbox) => ({
     ...checkbox,
     className: "sf-controls-checkbox__label--shape",
    })),
  },
  {
   name: "ヒール",
   subGroups: [
    {
     // 親子のラップ要素
     name: "ヒール低め（～4.5cm）",
     subGroups: [
      {
       // 親要素
       name: "ヒールレンジ",
       checkboxes: checkboxes
        .filter((checkbox) => ["item_heel_range-0"].includes(checkbox.id))
        .map((checkbox) => ({
         ...checkbox,
         className: "sf-controls-checkbox__label--parent",
        })),
      },
      {
       // 子要素
       name: "ヒールの高さ",
       checkboxes: checkboxes
        .filter((checkbox) => ["item_heel_cm-0", "item_heel_cm-1", "item_heel_cm-2", "item_heel_cm-3", "item_heel_cm-4", "item_heel_cm-5", "item_heel_cm-6"].includes(checkbox.id))
        .map((checkbox) => ({
         ...checkbox,
         className: "sf-controls-checkbox__label--child",
        })),
      },
     ],
    },
    {
     // 親子のラップ要素
     name: "ヒール高め（5.0cm～）",
     subGroups: [
      {
       // 親要素
       name: "ヒールレンジ",
       checkboxes: checkboxes
        .filter((checkbox) => ["item_heel_range-1"].includes(checkbox.id))
        .map((checkbox) => ({
         ...checkbox,
         className: "sf-controls-checkbox__label--parent",
        })),
      },
      {
       // 子要素
       name: "ヒールの高さ",
       checkboxes: checkboxes
        .filter((checkbox) => ["item_heel_cm-7", "item_heel_cm-8", "item_heel_cm-9", "item_heel_cm-10"].includes(checkbox.id))
        .map((checkbox) => ({
         ...checkbox,
         className: "sf-controls-checkbox__label--child",
        })),
      },
     ],
    },
   ],
  },
  {
   name: "ウィズ（足囲）",
   subGroups: [
    {
     // 親子のラップ要素
     name: "標準&ややゆったり（2E･3E）",
     subGroups: [
      {
       // 親要素
       name: "ウィズレンジ",
       checkboxes: checkboxes
        .filter((checkbox) => ["item_width_range-0"].includes(checkbox.id))
        .map((checkbox) => ({
         ...checkbox,
         className: "sf-controls-checkbox__label--parent",
        })),
      },
      {
       // 子要素
       name: "ウィズ",
       checkboxes: checkboxes
        .filter((checkbox) => ["item_width_e-0", "item_width_e-1"].includes(checkbox.id))
        .map((checkbox) => ({
         ...checkbox,
         className: "sf-controls-checkbox__label--child",
        })),
      },
     ],
    },
    {
     // 親子のラップ要素
     name: "ゆったり（4E･5E）",
     subGroups: [
      {
       // 親要素
       name: "ウィズレンジ",
       checkboxes: checkboxes
        .filter((checkbox) => ["item_width_range-1"].includes(checkbox.id))
        .map((checkbox) => ({
         ...checkbox,
         className: "sf-controls-checkbox__label--parent",
        })),
      },
      {
       // 子要素
       name: "ウィズ",
       checkboxes: checkboxes
        .filter((checkbox) => ["item_width_e-2", "item_width_e-3", "item_width_e-4"].includes(checkbox.id))
        .map((checkbox) => ({
         ...checkbox,
         className: "sf-controls-checkbox__label--child",
        })),
      },
     ],
    },
   ],
  },
  {
   name: "カラー",
   subGroups: [
    {
     // 親子のラップ要素
     name: "黒",
     subGroups: [
      {
       // 親要素
       name: "黒",
       checkboxes: checkboxes
        .filter((checkbox) => ["item_color_attribute_1-0"].includes(checkbox.id))
        .map((checkbox) => ({
         ...checkbox,
         className: "sf-controls-checkbox__label--parent",
        })),
      },
     ],
    },
    {
     // 親子のラップ要素
     name: "黒以外",
     subGroups: [
      {
       // 親要素
       name: "黒以外",
       checkboxes: checkboxes
        .filter((checkbox) => ["item_color_attribute_1-1"].includes(checkbox.id))
        .map((checkbox) => ({
         ...checkbox,
         className: "sf-controls-checkbox__label--parent",
        })),
      },
      {
       // 子要素
       name: "色属性2",
       checkboxes: checkboxes
        .filter((checkbox) =>
         [
          "item_color_attribute_2-0",
          "item_color_attribute_2-1",
          "item_color_attribute_2-2",
          "item_color_attribute_2-3",
          "item_color_attribute_2-4",
          "item_color_attribute_2-5",
          "item_color_attribute_2-6",
          "item_color_attribute_2-7",
         ].includes(checkbox.id)
        )
        .map((checkbox) => ({
         ...checkbox,
         className: "sf-controls-checkbox__label--child",
        })),
      },
     ],
    },
   ],
  },
  {
   name: "つま先",
   checkboxes: checkboxes
    .filter((checkbox) => checkbox.group === "item_toe")
    .map((checkbox) => ({
     ...checkbox,
     className: "sf-controls-checkbox__label--toe",
    })),
  },
  {
   name: "素材",
   checkboxes: checkboxes
    .filter((checkbox) => checkbox.group === "item_material")
    .map((checkbox) => ({
     ...checkbox,
     className: "sf-controls-checkbox__label--material",
    })),
  },
  {
   name: "インソール",
   checkboxes: checkboxes
    .filter((checkbox) => checkbox.group === "item_insole")
    .map((checkbox) => ({
     ...checkbox,
     className: "sf-controls-checkbox__label--insole",
    })),
  },
  {
   name: "デザイン",
   subGroups: [
    {
     name: "デザイン1",
     checkboxes: checkboxes
      .filter((checkbox) => checkbox.group?.startsWith("design_1"))
      .map((checkbox) => ({
       ...checkbox,
       className: "sf-controls-checkbox__label--design-1",
      })),
    },
    {
     name: "デザイン2",
     checkboxes: checkboxes
      .filter((checkbox) => checkbox.group?.startsWith("design_2"))
      .map((checkbox) => ({
       ...checkbox,
       className: "sf-controls-checkbox__label--design-2",
      })),
    },
   ],
  },
  {
   name: "うれしい機能からも絞り込みできます",
   checkboxes: checkboxes
    .filter((checkbox) => checkbox.group?.startsWith("features"))
    .map((checkbox) => ({
     ...checkbox,
     className: "sf-controls-checkbox__label--features",
    })),
  },
  {
   name: "こんなキーワードからも絞り込みできます",
   checkboxes: checkboxes
    .filter((checkbox) => checkbox.group?.startsWith("freeword"))
    .map((checkbox) => ({
     ...checkbox,
     className: "sf-controls-checkbox__label--freeword",
    })),
  },
 ];

 const updateCounts = (checkboxes, filtered) => {
  return checkboxes.map((checkbox) => {
   if (checkbox.id) {
    const [key, subKey] = checkbox.id.split("-");
    const count = filtered.filter((item) => {
     if (item[key] && typeof item[key] === "object") {
      return item[key]?.[subKey] === checkbox.checkbox_name;
     }
     return item[key] === checkbox.checkbox_name;
    }).length;

    // count が 0 の場合、チェックを外す
    let checked = checkbox.checked;
    if (count === 0 && checkbox.checked) {
     checked = false;
    }

    return { ...checkbox, count, checked };
   }
   return checkbox;
  });
 };

 const isMatch = (category, key, item) => {
  const filters = checkboxes.filter((checkbox) => checkbox.group === category && checkbox.checked).map((checkbox) => checkbox.checkbox_name);
  return filters.length === 0 || filters.includes(item[key]);
 };

 const isNestedMatch = (key, subKey, item) => {
  const filters = checkboxes.filter((checkbox) => checkbox.group === `${key}-${subKey}` && checkbox.checked).map((checkbox) => checkbox.checkbox_name);
  return filters.length === 0 || filters.includes(item[key]?.[subKey]);
 };

 const getChildCheckboxIds = (parentId) => {
  const parentElement = document.querySelector(`label[for='${parentId}']`).closest(".sf-controls-checkbox__label-group-sub");
  const childCheckboxElements = parentElement.querySelectorAll(".sf-controls-checkbox__label-group-subsub .sf-controls-checkbox__label--child input[type='checkbox']");
  return Array.from(childCheckboxElements).map((el) => el.id);
 };

 const getParentCheckboxId = (childId) => {
  const parentElement = document.querySelector(`label[for='${childId}']`).closest(".sf-controls-checkbox__label-group-sub");
  const parentCheckboxElement = parentElement.querySelector(".sf-controls-checkbox__label--parent input[type='checkbox']");
  return parentCheckboxElement ? parentCheckboxElement.id : null;
 };

 const getSiblingCheckboxIds = (childId) => {
  const parentElement = document.querySelector(`label[for='${childId}']`).closest(".sf-controls-checkbox__label-group-sub");
  const siblingCheckboxElements = parentElement.querySelectorAll(".sf-controls-checkbox__label-group-subsub .sf-controls-checkbox__label--child input[type='checkbox']");
  return Array.from(siblingCheckboxElements)
   .map((el) => el.id)
   .filter((id) => id !== childId);
 };

 // チェックボックスの状態変更時の処理
 const handleCheckboxChange = (id) => {
  setCheckboxes((prevCheckboxes) => {
   // チェックボックスの状態を反転
   const updatedCheckboxes = prevCheckboxes.map((checkbox) => {
    if (checkbox.id === id) {
     return { ...checkbox, checked: !checkbox.checked };
    }
    return checkbox;
   });

   const changedCheckbox = updatedCheckboxes.find((checkbox) => checkbox.id === id);
   const isChecked = changedCheckbox.checked;
   const labelElement = document.querySelector(`label[for='${id}']`);
   const isParent = labelElement.classList.contains("sf-controls-checkbox__label--parent");
   const isChild = labelElement.classList.contains("sf-controls-checkbox__label--child");

   if (isParent) {
    // 親チェックボックスが変更された場合、子チェックボックスを更新
    const childCheckboxIds = getChildCheckboxIds(id);

    // is-child-opened クラスの追加（削除はしない）
    const parentElement = labelElement.closest(".sf-controls-checkbox__label-group-sub");
    if (parentElement) {
     if (isChecked) {
      parentElement.classList.add("is-child-opened");
     }
     // チェックが外れてもクラスは削除しない
    }

    return updatedCheckboxes.map((checkbox) => {
     if (childCheckboxIds.includes(checkbox.id)) {
      return { ...checkbox, checked: isChecked };
     }
     return checkbox;
    });
   } else if (isChild) {
    // 子チェックボックスが変更された場合、親チェックボックスを更新
    const parentCheckboxId = getParentCheckboxId(id);
    const siblingCheckboxIds = getSiblingCheckboxIds(id);

    // 現在の子要素と兄弟の子要素のIDをすべて取得
    const childCheckboxIds = [id, ...siblingCheckboxIds];

    // 子要素のいずれかがチェックされているかを確認
    const anyChildChecked = childCheckboxIds.some((childId) => {
     const childCheckbox = updatedCheckboxes.find((chk) => chk.id === childId);
     return childCheckbox.checked;
    });

    const updatedCheckboxesWithParent = updatedCheckboxes.map((checkbox) => {
     if (checkbox.id === parentCheckboxId) {
      return { ...checkbox, checked: anyChildChecked };
     }
     return checkbox;
    });

    // 子チェックボックスの変更時には is-child-opened クラスは変更しない

    return updatedCheckboxesWithParent;
   }

   return updatedCheckboxes;
  });
 };

 // GA4イベント送信関数（確認用）（チェックボックスの操作ごとにGa4トラッキング 2/n）
 const gtag =
  window.gtag ||
  function () {
   (window.dataLayer = window.dataLayer || []).push(arguments);
  };

 // 条件クリアボタンの処理
 const handleClearAll = () => {
  setCheckboxes(checkboxes.map((checkbox) => ({ ...checkbox, checked: false })));
  const params = new URLSearchParams(location.search);

  // initialCheckboxesからフィルタキーを取得し、削除
  const filterKeys = new Set(checkboxes.map((checkbox) => checkbox.group));
  filterKeys.forEach((key) => {
   params.delete(key);
  });

  navigate({ search: params.toString() }, { replace: true });
 };

 useEffect(() => {
  const selectedFilters = checkboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.checkbox_name);

  const filterCategories = {
   item_heel_range: [],
   item_heel_cm: [],
   item_width_range: [],
   item_width_e: [],
   item_toe: [],
   item_material: [],
   item_insole: [],
   item_color_attribute_1: [],
   item_color_attribute_2: [],
   shape: [],
   design_1: [],
   design_2: [],
   features: [],
   freeword: [],
  };

  selectedFilters.forEach((filter) => {
   ItemsData.forEach((item) => {
    Object.keys(filterCategories).forEach((key) => {
     if (typeof item[key] === "object" && item[key] !== null) {
      Object.values(item[key]).forEach((value) => {
       if (value === filter) {
        filterCategories[key].push(filter);
       }
      });
     } else if (item[key] === filter) {
      filterCategories[key].push(filter);
     }
    });
   });
  });

  const filtered = ItemsData.filter((item) => {
   return (
    isMatch("item_heel_range", "item_heel_range", item) &&
    isMatch("item_heel_cm", "item_heel_cm", item) &&
    isMatch("item_width_range", "item_width_range", item) &&
    isMatch("item_width_e", "item_width_e", item) &&
    isMatch("item_toe", "item_toe", item) &&
    isMatch("item_material", "item_material", item) &&
    isMatch("item_insole", "item_insole", item) &&
    isMatch("item_color_attribute_1", "item_color_attribute_1", item) &&
    isMatch("item_color_attribute_2", "item_color_attribute_2", item) &&
    isNestedMatch("shape", "shape_1", item) &&
    isNestedMatch("shape", "shape_2", item) &&
    isNestedMatch("design_1", "design_1_1", item) &&
    isNestedMatch("design_1", "design_1_2", item) &&
    isNestedMatch("design_1", "design_1_3", item) &&
    isNestedMatch("design_1", "design_1_4", item) &&
    isNestedMatch("design_2", "design_2_1", item) &&
    isNestedMatch("design_2", "design_2_2", item) &&
    isNestedMatch("design_2", "design_2_3", item) &&
    isNestedMatch("design_2", "design_2_4", item) &&
    isNestedMatch("design_2", "design_2_5", item) &&
    isNestedMatch("features", "features_1", item) &&
    isNestedMatch("features", "features_2", item) &&
    isNestedMatch("features", "features_3", item) &&
    isNestedMatch("features", "features_4", item) &&
    isNestedMatch("features", "features_5", item) &&
    isNestedMatch("features", "features_6", item) &&
    isNestedMatch("features", "features_7", item) &&
    isNestedMatch("features", "features_8", item) &&
    isNestedMatch("features", "features_9", item) &&
    isNestedMatch("freeword", "freeword_1", item) &&
    isNestedMatch("freeword", "freeword_2", item) &&
    isNestedMatch("freeword", "freeword_3", item) &&
    isNestedMatch("freeword", "freeword_4", item) &&
    isNestedMatch("freeword", "freeword_5", item) &&
    isNestedMatch("freeword", "freeword_6", item) &&
    isNestedMatch("freeword", "freeword_7", item) &&
    isNestedMatch("freeword", "freeword_8", item)
   );
  });

  setFilteredItems(filtered);

  const updatedCheckboxes = updateCounts(checkboxes, filtered);

  if (JSON.stringify(checkboxes) !== JSON.stringify(updatedCheckboxes)) {
   setCheckboxes(updatedCheckboxes);
  }
 }, [checkboxes, ItemsData]);

 const filteredItemCount = filteredItems.length;

 //  商品のソートロジック
 const sortedItems = useSortedItems(filteredItems);

 if (loading) {
  return <Loading />;
 }

 // フィルタパラメータの計測を行う関数（検索モーダルを閉じたら、まとめて送信トラッキング 1/n）
 const trackFilters = () => {
  const activeFilters = checkboxes
   .filter((checkbox) => checkbox.checked)
   .map((checkbox) => ({
    group: checkbox.group,
    name: checkbox.checkbox_name,
   }));

  if (activeFilters.length > 0) {
   gtag("event", "filters_apply", {
    event_category: "Filters",
    event_label: "Filters Applied",
    value: JSON.stringify(activeFilters),
   });
   //  console.log("Filters applied:", activeFilters);
  } else {
   gtag("event", "filters_apply", {
    event_category: "Filters",
    event_label: "Filters Applied",
    value: "Unchecked",
   });
   //  console.log("No filters applied");
  }
 };

 // トグルボタンをクリックしたときに検索エリアを開閉する
 const toggleSf = (event) => {
  event.preventDefault();
  const modalSfContainer = document.querySelector("#modal_search_filters_container");
  setIsSfOpen(!isSfOpen);

  if (!isSfOpen) {
   modalSfContainer.classList.remove("is-open");
   trackFilters(); // モーダルを閉じたときにフィルタを計測（検索モーダルを閉じたら、まとめて送信トラッキング 2/n）
  } else {
   modalSfContainer.classList.add("is-open");
  }
 };

 return (
  <>
   {!isSfOpen && <div className="sf-controls-toggle__overlay" onClick={toggleSf} tabIndex="-1"></div>}
   <div className="sf-inner">
    <div className="sf-head">
     <button id="search_toggle_button" className="sf-head__toggle-button" onClick={toggleSf} aria-controls="sf-controls" aria-expanded={!isSfOpen}>
      <span>{isSfOpen ? "絞り込み画面を開く" : "絞り込み画面を閉じて商品を見る"}</span>
     </button>
     <p className="sf-head__matching-count">
      <span className="sf-head__matching-count-title">検索結果</span>
      <span className="sf-head__matching-count-filtered">{filteredItemCount}</span>
      <span className="sf-head__matching-count-slash"></span>
      <span className="sf-head__matching-count-all">{ItemsData.length}</span>
      <small>件</small>
     </p>
    </div>

    <div className="sf-result">
     <div className="sf-result__body">
      <button className="sf-result__clear-all" onClick={handleClearAll} aria-label="条件をクリア">
       条件をクリア
      </button>
      {[...checkboxes].filter((checkbox) => checkbox.checked).length > 0 && (
       <div className="sf-result__inner">
        <ul className="sf-result__list">
         {[...checkboxes]
          .filter((checkbox) => {
           const element = document.querySelector(`input#${checkbox.id}`);
           return checkbox.checked && element && parseInt(element.getAttribute("data-count-rest"), 10) > 0;
          })
          .sort((a, b) => {
           const orderA = resultOrder.indexOf(a.group);
           const orderB = resultOrder.indexOf(b.group);
           return orderA - orderB;
          })
          .map((checkbox) => (
           <li key={checkbox.id}>
            <button className="sf-result__remove" onClick={() => handleCheckboxChange(checkbox.id, checkbox.checkbox_name, checkbox.group)} aria-label={`条件[${checkbox.checkbox_name}]を削除する`}>
             <span>{checkbox.checkbox_name}</span>
            </button>
           </li>
          ))}
        </ul>
       </div>
      )}
     </div>
    </div>

    <div id="search_controls_toggle" className={`sf-controls-toggle ${!isSfOpen ? "is-open" : ""}`} aria-hidden={isSfOpen}>
     <div id="search_controls" className="sf-controls">
      <div id="sf_controls_checkboxes" className="sf-controls-checkboxes" onScroll={debouncedHandleScroll}>
       {filterGroups.map((group, index) => (
        <div key={index} className="sf-controls-checkbox" data-checkbox-name={group.name}>
         <fieldset>
          <div className="sf-controls-checkbox__inner">
           <legend>
            <span>{group.name}</span>
           </legend>
           <div className="sf-controls-checkbox__label-group">
            {group.subGroups
             ? group.subGroups.map((subGroup, subIndex) => (
                <div key={subIndex} className="sf-controls-checkbox__label-group-sub" aria-label={subGroup.name}>
                 {subGroup.subGroups
                  ? subGroup.subGroups.map((subSubGroup, subSubIndex) => (
                     <div key={`subSubGroup-${subSubIndex}`} className="sf-controls-checkbox__label-group-subsub" aria-label={subSubGroup.name}>
                      {subSubGroup.checkboxes.map((checkbox) => (
                       <LabelCheckbox key={checkbox.id} checkbox={checkbox} isSfOpen={isSfOpen} handleCheckboxChange={handleCheckboxChange} />
                      ))}
                     </div>
                    ))
                  : subGroup.checkboxes.map((checkbox) => <LabelCheckbox key={checkbox.id} checkbox={checkbox} isSfOpen={isSfOpen} handleCheckboxChange={handleCheckboxChange} />)}
                </div>
               ))
             : group.checkboxes.map((checkbox) => <LabelCheckbox key={checkbox.id} checkbox={checkbox} isSfOpen={isSfOpen} handleCheckboxChange={handleCheckboxChange} />)}
           </div>
          </div>
         </fieldset>
        </div>
       ))}
      </div>
     </div>
    </div>
   </div>

   {filteredItemCount === 0 && (
    <div className="sf-result-error">
     <p className="sf-result-error__message">
      <span>検索条件に一致する商品が</span>
      <span>見つかりませんでした。</span>
     </p>
     <p className="sf-result-error__info">
      <span>絞り込み条件を変更して</span>
      <span>再検索してください。</span>
     </p>
    </div>
   )}

   <div className="p-cards">
    {sortedItems.map((item, index) => (
     <ItemCard key={index} props={item} className="--card-search" />
    ))}
   </div>
  </>
 );
}

export default Filters;
